/**
 * Knowledge Unit Admin API Client (WS-4)
 *
 * Typed client for the cross-user shared knowledge repository moderation
 * endpoints. Mirrors the fetchJson-over-authenticatedFetch pattern used by
 * orchestratorClient.ts — the Bearer token is attached automatically via the
 * app's token provider wiring (see App.tsx / authenticatedFetch).
 *
 * All endpoints are admin-only (require the Admin role in the JWT):
 *   GET  /api/admin/orchestrator/knowledge?status=&taskType=&page=&pageSize=
 *   GET  /api/admin/orchestrator/knowledge/{id}
 *   POST /api/admin/orchestrator/knowledge/{id}/approve   body { expiresAtUtc? }
 *   POST /api/admin/orchestrator/knowledge/{id}/retire
 *   PUT  /api/admin/orchestrator/knowledge/{id}           body { taskType?, industry?, jurisdiction?, title?, body?, expiresAtUtc? }
 */

import { authenticatedFetch } from './authenticatedFetch';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }

  return response.json() as Promise<T>;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

const KNOWLEDGE_BASE = '/api/admin/orchestrator/knowledge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type KnowledgeStatus = 'draft' | 'approved' | 'retired';
export type ScrubStatus = 'clean' | 'flagged';

export interface KnowledgeUnitAdminDto {
  knowledgeUnitId: string;
  version: number;
  status: KnowledgeStatus;
  taskType: string;
  industry?: string | null;
  jurisdiction?: string | null;
  title: string;
  body: string;
  sourceJobIds?: string | null;
  qualityScore?: number | null;
  scrubStatus: ScrubStatus;
  proposedByModel?: string | null;
  usageCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  approvedAtUtc?: string | null;
  approvedBy?: string | null;
  retiredAtUtc?: string | null;
  expiresAtUtc?: string | null;
}

/** Paged list envelope returned by the knowledge list endpoint. */
export interface KnowledgePagedResult {
  total: number;
  page: number;
  pageSize: number;
  items: KnowledgeUnitAdminDto[];
}

export interface GetKnowledgeParams {
  /** Status filter; omit for all statuses. */
  status?: KnowledgeStatus;
  /** Optional taskType filter. */
  taskType?: string;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// List + detail
// ---------------------------------------------------------------------------

export function getKnowledgeUnits(
  params: GetKnowledgeParams = {},
): Promise<KnowledgePagedResult> {
  const qs = buildQuery({
    status: params.status,
    taskType: params.taskType,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });
  return fetchJson<KnowledgePagedResult>(`${KNOWLEDGE_BASE}${qs}`);
}

export function getKnowledgeUnit(id: string): Promise<KnowledgeUnitAdminDto> {
  return fetchJson<KnowledgeUnitAdminDto>(`${KNOWLEDGE_BASE}/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface ApproveKnowledgeRequest {
  /** Optional expiry — ISO-8601 UTC string, or null for no expiry. */
  expiresAtUtc?: string | null;
}

export function approveKnowledgeUnit(
  id: string,
  req: ApproveKnowledgeRequest = {},
): Promise<KnowledgeUnitAdminDto> {
  return fetchJson<KnowledgeUnitAdminDto>(`${KNOWLEDGE_BASE}/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ expiresAtUtc: req.expiresAtUtc ?? null }),
  });
}

export function retireKnowledgeUnit(id: string): Promise<KnowledgeUnitAdminDto> {
  return fetchJson<KnowledgeUnitAdminDto>(`${KNOWLEDGE_BASE}/${encodeURIComponent(id)}/retire`, {
    method: 'POST',
  });
}

export interface UpdateKnowledgeRequest {
  taskType?: string;
  industry?: string | null;
  jurisdiction?: string | null;
  title?: string;
  body?: string;
  expiresAtUtc?: string | null;
}

export function updateKnowledgeUnit(
  id: string,
  req: UpdateKnowledgeRequest,
): Promise<KnowledgeUnitAdminDto> {
  return fetchJson<KnowledgeUnitAdminDto>(`${KNOWLEDGE_BASE}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
}
